import { safeJson } from "src/app/helpers/Index";
import { faCalendarRange, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React from "react";
import { SALE_WINDOW_STATUS_LABELS, SaleWindowService, SaleWindowStatus } from "src/app/services/Index";
import "./SaleWindows.css";

type Props = {
	/** SaleWindowParent.SeasonTicketProduct | Fixture | Event */
	parentType: number;
	parentId: number;
	/** Season ticket windows carry validity and transfer; fixture windows don't. */
	showValidity?: boolean;
	showPriority?: boolean;
	onError?: (message: string) => void;
};

type State = {
	windows: any[];
	isLoading: boolean;
	saving: boolean;
	showDialog: boolean;
	edit: any;
};

const shortDateTime = (value: any) => {
	if (!value) return "—";
	const d = new Date(value);
	return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" });
};

/**
 * Sale windows for one thing — a season ticket product, a fixture, an event.
 *
 * Embedded rather than a page of its own, because a window only means anything
 * next to what it sells. The columns deliberately separate "on sale" from
 * "admits you to": that distinction is the entire reason the entity exists,
 * and a UI that blurred it would put us back where PB-9 started.
 */
export class SaleWindows extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { windows: [], isLoading: true, saving: false, showDialog: false, edit: null };
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		try {
			const windows = await safeJson(SaleWindowService.getForParent(this.props.parentType, this.props.parentId));
			this.setState({ windows: Array.isArray(windows) ? windows : [], isLoading: false });
		} catch {
			this.setState({ windows: [], isLoading: false });
		}
	}

	open(window?: any) {
		this.setState({
			edit: window
				? {
					...window,
					onSaleFrom: window.onSaleFrom ? new Date(window.onSaleFrom) : null,
					onSaleTo: window.onSaleTo ? new Date(window.onSaleTo) : null,
					validFrom: window.validFrom ? new Date(window.validFrom) : null,
					validTo: window.validTo ? new Date(window.validTo) : null
				}
				: {
					hasPriority: false, hasValidityPeriod: false, isOnSaleToFans: true,
					isTemporaryTransferEnabled: false, name: "",
					sortOrder: this.state.windows.length, status: SaleWindowStatus.Draft
				},
			showDialog: true
		});
	}

	async save() {
		const w = this.state.edit;
		if (!w?.name?.trim()) return;

		this.setState({ saving: true });
		const payload = {
			description: w.description || null,
			hasPriority: !!w.hasPriority,
			hasValidityPeriod: !!w.hasValidityPeriod,
			isOnSaleToFans: !!w.isOnSaleToFans,
			isTemporaryTransferEnabled: !!w.isTemporaryTransferEnabled,
			name: w.name.trim(),
			onSaleFrom: w.onSaleFrom || null,
			onSaleTo: w.onSaleTo || null,
			sortOrder: Number(w.sortOrder) || 0,
			status: Number(w.status) || 0,
			validFrom: w.hasValidityPeriod ? w.validFrom : null,
			validTo: w.hasValidityPeriod ? w.validTo : null
		};

		const res = w.id
			? await SaleWindowService.update(w.id, payload)
			: await SaleWindowService.add(this.props.parentType, this.props.parentId, payload);
		this.setState({ saving: false });

		if (!res.ok) {
			const message = await res.text();
			this.props.onError?.(message || "Couldn't save that sale window.");
			return;
		}
		this.setState({ edit: null, showDialog: false });
		this.load();
	}

	async remove(window: any) {
		if (!globalThis.confirm(`Remove "${window.name}"? Anything already sold in it stays valid.`)) return;
		await SaleWindowService.remove(window.id);
		this.load();
	}

	render() {
		const { windows, isLoading } = this.state;
		const { showValidity, showPriority } = this.props;
		const edit = this.state.edit;

		return (
			<div className="sw-wrap">
				<div className="sw-head">
					<h4>Sale windows</h4>
					<Button className="p-button-text" icon="pi pi-plus" label="Add window" onClick={() => this.open()} />
				</div>

				{isLoading && <p className="sw-none">Loading&hellip;</p>}

				{!isLoading && windows.length === 0 &&
					<div className="sw-empty">
						<FontAwesomeIcon icon={faCalendarRange} />
						<p>No sale windows. Add one to control when this goes on sale &mdash; and, if it needs it, what the pass covers.</p>
					</div>}

				{windows.length > 0 &&
					<div className="sw-table">
						<div className="sw-tr sw-th">
							<span>Name</span><span>On sale</span>
							{showValidity && <span>Admits you to</span>}
							<span>Who</span><span>Status</span><span />
						</div>
						{windows.map((w) => (
							<div className={`sw-tr${w.isOpenNow ? " open" : ""}`} key={`w-${w.id}`}>
								<span>
									{w.name}
									{w.isOpenNow && <small className="sw-live">selling now</small>}
								</span>
								<span>{shortDateTime(w.onSaleFrom)} &ndash; {shortDateTime(w.onSaleTo)}</span>
								{showValidity &&
									<span>{w.hasValidityPeriod
										? `${shortDateTime(w.validFrom)} – ${shortDateTime(w.validTo)}`
										: <em className="sw-muted">product dates</em>}</span>}
								<span>{w.isOnSaleToFans
									? (w.hasPriority ? "Season ticket holders" : "Anyone")
									: <em className="sw-muted">Club only</em>}</span>
								<span className={`sw-status s${w.status}`}>{SALE_WINDOW_STATUS_LABELS[w.status]}</span>
								<span className="sw-actions">
									<Button className="p-button-text" label="Edit" onClick={() => this.open(w)} style={{ padding: "2px 6px" }} />
									<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.remove(w)} style={{ padding: "2px 6px" }} />
								</span>
							</div>
						))}
					</div>}

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
				</>} header={edit?.id ? "Edit sale window" : "New sale window"} modal onHide={() => this.setState({ showDialog: false })} style={{ width: 520 }} visible={this.state.showDialog}>
					{edit && <>
						<label className="sw-label">Name</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...edit, name: e.target.value } })} placeholder="Early Bird" style={{ marginBottom: 16, width: "100%" }} value={edit.name || ""} />

						<div className="sw-fieldset">
							<span className="sw-legend">When it&rsquo;s on sale</span>
							<div className="sw-two">
								<div>
									<label className="sw-label">Opens</label>
									<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, onSaleFrom: e.value } })} showButtonBar showTime style={{ width: "100%" }} value={edit.onSaleFrom} />
								</div>
								<div>
									<label className="sw-label">Closes</label>
									<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, onSaleTo: e.value } })} showButtonBar showTime style={{ width: "100%" }} value={edit.onSaleTo} />
								</div>
							</div>
							<p className="sw-hint">Leave either blank to leave that end open.</p>
						</div>

						{showValidity &&
							<div className="sw-fieldset">
								<span className="sw-legend">What it admits you to</span>
								<div className="sw-switch">
									<InputSwitch checked={!!edit.hasValidityPeriod} onChange={(e) => this.setState({ edit: { ...edit, hasValidityPeriod: e.value } })} />
									<label>Cover its own dates, not the product&rsquo;s</label>
								</div>
								<p className="sw-hint">
									An Early Bird sold in March for a season starting in July needs this. Without it, a pass covers whatever the product says.
								</p>
								{edit.hasValidityPeriod &&
									<div className="sw-two" style={{ marginTop: 12 }}>
										<div>
											<label className="sw-label">Valid from</label>
											<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, validFrom: e.value } })} style={{ width: "100%" }} value={edit.validFrom} />
										</div>
										<div>
											<label className="sw-label">Valid to</label>
											<Calendar dateFormat="dd/mm/yy" onChange={(e) => this.setState({ edit: { ...edit, validTo: e.value } })} style={{ width: "100%" }} value={edit.validTo} />
										</div>
									</div>}
							</div>}

						<div className="sw-fieldset">
							<span className="sw-legend">Who can buy</span>
							<div className="sw-switch">
								<InputSwitch checked={!!edit.isOnSaleToFans} onChange={(e) => this.setState({ edit: { ...edit, isOnSaleToFans: e.value } })} />
								<label>Fans can buy this themselves</label>
							</div>
							<p className="sw-hint">Off means you sell it &mdash; over the counter or on the phone. It never shows on the storefront.</p>

							{showPriority &&
								<div className="sw-switch" style={{ marginTop: 12 }}>
									<InputSwitch checked={!!edit.hasPriority} onChange={(e) => this.setState({ edit: { ...edit, hasPriority: e.value } })} />
									<label>Season ticket holders only</label>
								</div>}

							{showValidity &&
								<div className="sw-switch" style={{ marginTop: 12 }}>
									<InputSwitch checked={!!edit.isTemporaryTransferEnabled} onChange={(e) => this.setState({ edit: { ...edit, isTemporaryTransferEnabled: e.value } })} />
									<label>Holders can lend their seat for a fixture</label>
								</div>}
						</div>

						<label className="sw-label">Status</label>
						<select className="sw-select" onChange={(e) => this.setState({ edit: { ...edit, status: Number(e.target.value) } })} value={edit.status}>
							<option value={SaleWindowStatus.Draft}>Draft &mdash; not selling</option>
							<option value={SaleWindowStatus.OnSale}>On sale</option>
							<option value={SaleWindowStatus.Renewal}>Renewal &mdash; existing holders</option>
							<option value={SaleWindowStatus.Archived}>Archived</option>
						</select>
						<p className="sw-hint">Draft and Archived never sell, whatever the dates say.</p>
					</>}
				</Dialog>
			</div>
		);
	}
}
